import Axios from "axios";
import { message } from "antd";
import {
	AiChatSubject,
	AiConversation,
	AiConversationDetail,
	AiKnowledgeEntry,
	AiKnowledgePayload,
} from "../entity/aiChatAdmin.entity";

interface ChatAdminResponse<T> {
	code: number;
	msg: string;
	data: T | null;
}

export interface AiItemList<T> {
	items: T[];
}

export interface AiConversationPage {
	records: AiConversation[];
	total: number;
	size: number;
	current: number;
	pages: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === "object" && !Array.isArray(value);
}

function errorMessage(error: unknown, fallback: string) {
	if (isRecord(error)) {
		const response = isRecord(error.response) ? error.response : undefined;
		const responseData = response && isRecord(response.data) ? response.data : undefined;
		if (typeof responseData?.msg === "string" && responseData.msg) return responseData.msg;
		if (typeof error.message === "string" && error.message) return error.message;
	}
	return fallback;
}

export function parseChatAdminResponse<T>(body: unknown): T {
	if (!isRecord(body)) throw new Error("AI 管理接口响应格式不正确");
	const code = body.code;
	const msg = typeof body.msg === "string" ? body.msg : "";
	if (code !== 200) {
		throw new Error(msg || "AI 管理接口请求失败");
	}
	if (!("data" in body) || body.data === null || body.data === undefined) {
		throw new Error("AI 管理接口缺少 data 数据");
	}
	return (body as unknown as ChatAdminResponse<T>).data as T;
}

export function parseItemList<T>(data: unknown, label: string): AiItemList<T> {
	if (!isRecord(data) || !Array.isArray(data.items)) {
		throw new Error(`${label}结构不正确，缺少 items`);
	}
	return { items: data.items as T[] };
}

export function parseConversationPage(data: unknown): AiConversationPage {
	if (!isRecord(data) || !Array.isArray(data.records)) {
		throw new Error("聊天记录列表结构不正确，缺少 records");
	}
	for (const key of ["total", "size", "current", "pages"] as const) {
		if (typeof data[key] !== "number" || !Number.isFinite(data[key])) {
			throw new Error(`聊天记录列表结构不正确，缺少 ${key}`);
		}
	}
	return {
		records: data.records as AiConversation[],
		total: data.total as number,
		size: data.size as number,
		current: data.current as number,
		pages: data.pages as number,
	};
}

export function parseConversationDetail(data: unknown): AiConversationDetail {
	if (
		!isRecord(data) ||
		!isRecord(data.conversation) ||
		!Array.isArray(data.messages)
	) {
		throw new Error("聊天记录详情结构不正确");
	}
	return {
		conversation: data.conversation as unknown as AiConversation,
		messages: data.messages as AiConversationDetail["messages"],
	};
}

const chatApi = Axios.create({
	baseURL: process.env.REACT_APP_CHAT_API || "",
	timeout: 1000 * 60 * 2,
});

chatApi.interceptors.response.use(
	(response) => response,
	(error) => {
		message.error(errorMessage(error, "AI 管理接口网络异常"));
		return Promise.reject(error);
	}
);

async function unwrap<T>(promise: Promise<{ data: unknown }>): Promise<T> {
	const response = await promise;
	try {
		return parseChatAdminResponse<T>(response.data);
	} catch (error) {
		message.error(errorMessage(error, "AI 管理接口响应解析失败"));
		throw error;
	}
}

class AiChatAdminService {
	listKnowledge = async (params: {
		q?: string;
		enabled?: boolean;
		limit?: number;
		offset?: number;
	}) => {
		const data = await unwrap<unknown>(
			chatApi.get("/api/admin/knowledge", { params })
		);
		return parseItemList<AiKnowledgeEntry>(data, "AI 知识库列表");
	};

	createKnowledge = (data: AiKnowledgePayload) => {
		return unwrap<AiKnowledgeEntry>(chatApi.post("/api/admin/knowledge", data));
	};

	updateKnowledge = (id: number, data: Partial<AiKnowledgePayload>) => {
		return unwrap<AiKnowledgeEntry>(chatApi.patch(`/api/admin/knowledge/${id}`, data));
	};

	deleteKnowledge = (id: number) => {
		return unwrap<{ deleted: boolean }>(chatApi.delete(`/api/admin/knowledge/${id}`));
	};

	listSubjects = async (params: {
		q?: string;
		blocked?: boolean;
		limit?: number;
		offset?: number;
	}) => {
		const data = await unwrap<unknown>(
			chatApi.get("/api/admin/subjects", { params })
		);
		return parseItemList<AiChatSubject>(data, "AI 用户列表");
	};

	updateSubject = (subjectKey: string, data: { blocked?: boolean; reason?: string }) => {
		return unwrap<{ item: AiChatSubject }>(
			chatApi.patch(`/api/admin/subjects/${encodeURIComponent(subjectKey)}`, data)
		);
	};

	listConversations = async (params: {
		q?: string;
		subjectKey?: string;
		limit?: number;
		offset?: number;
	}) => {
		const data = await unwrap<unknown>(
			chatApi.get("/api/admin/conversations", { params })
		);
		return parseConversationPage(data);
	};

	getConversation = async (id: string) => {
		const data = await unwrap<unknown>(
			chatApi.get(`/api/admin/conversations/${encodeURIComponent(id)}`)
		);
		return parseConversationDetail(data);
	};

	deleteConversation = (id: string) => {
		return unwrap<{ deleted: boolean }>(
			chatApi.delete(`/api/admin/conversations/${encodeURIComponent(id)}`)
		);
	};

	purgeExpired = () => {
		return unwrap<{ purge: { deletedConversations: number; at: string } }>(
			chatApi.post("/api/admin/maintenance")
		);
	};
}

export default new AiChatAdminService();
